import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
import MeasurementEditView from "./Measurementseditview";
import { IsValidPermissions } from "Routes/AuthProtected";

import {
  useDeleteMeasurementByIdMutation,
  useGetMeasurementsQuery,
  useGetMeasurementByIdQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface Measurement {
  id: number;
  name: string;
  symbol?: string | null;
  description?: string | null;
  created_at: string;
  modified_at?: string | null;
}

const MeasurementsList: React.FC = () => {
  // local UI state
  const [data, setData] = useState<Measurement[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentMeasurementId, setCurrentMeasurementId] = useState<
    number | null
  >(null);

  // view modal state
  const [viewMeasurementId, setViewMeasurementId] = useState<number | null>(
    null
  );
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [pageState, setPageState] = useState({ page: 1, pageSize: 30, search: "" });
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const debounce = window.setTimeout(() => {
      setPageState((previous) => ({ ...previous, page: 1, search: searchInput.trim() }));
    }, 300);
    return () => window.clearTimeout(debounce);
  }, [searchInput]);

  // List hook
  const {
    data: measurementsResponse,
    isLoading: loadingMeasurements,
    refetch: refetchMeasurements,
  } = useGetMeasurementsQuery(pageState);
  const [deleteMeasurement] = useDeleteMeasurementByIdMutation();

  // detail hook for view modal (skip when no id)
  const { data: measurementDetail, isFetching: fetchingMeasurementDetail } =
    useGetMeasurementByIdQuery(viewMeasurementId ?? undefined, {
      skip: !viewMeasurementId,
    });

  // keep local copy sorted by id ascending
  useEffect(() => {
    if (measurementsResponse?.results) {
      const sorted = [...measurementsResponse.results].sort(
        (a, b) => Number(a.id) - Number(b.id)
      );
      setData(sorted);
    }
  }, [measurementsResponse]);

  // handlers for edit/create modal
  const openCreateModal = () => {
    setCurrentMeasurementId(null);
    setIsEditMode(false);
    setShowEditModal(true);
  };

  const openEditModal = (id: number) => {
    setCurrentMeasurementId(id);
    setIsEditMode(true);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setCurrentMeasurementId(null);
    setIsEditMode(false);
  };

  const handleSaved = () => {
    // close edit modal and refresh list
    closeEditModal();
    refetchMeasurements();
  };

  // handlers for view modal
  const openViewModal = (id: number) => {
    setViewMeasurementId(id);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewMeasurementId(null);
  };

  const handleDelete = async (measurement: Measurement) => {
    if (!window.confirm(`Delete measurement "${measurement.name}"? This cannot be undone.`)) return;
    try {
      await deleteMeasurement(measurement.id).unwrap();
      toast.success("Measurement deleted successfully.");
      refetchMeasurements();
    } catch (error: any) {
      toast.error(error?.data?.detail || error?.data?.error || "Failed to delete measurement.");
    }
  };

  const baseColumns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      {
        Header: "Name",
        accessor: "name",
        Cell: (row: any) => (
          <span className="text-body">{row.row.original.name}</span>
        ),
      },
      { Header: "Symbol", accessor: "symbol" },
      {
        Header: "Description",
        accessor: "description",
        Cell: (cell: any) =>
          cell.row.original.description ? (
            <span>{cell.row.original.description}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        Header: "Created",
        accessor: "created_at",
        Cell: (cell: any) =>
          new Date(cell.row.original.created_at).toLocaleDateString(),
      },
      {
        Header: "Modified",
        accessor: "modified_at",
        Cell: (cell: any) =>
          cell.row.original.modified_at
            ? new Date(cell.row.original.modified_at).toLocaleDateString()
            : "-",
      },
      {
        Header: "Action",
        Cell: (cellProps: any) => {
          const m: Measurement = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                className="btn btn-soft-secondary btn-sm"
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                {(IsValidPermissions(["VIEW_ONLY_INVENTORY_MEASUREMENT"]) || IsValidPermissions(["VIEW_ALL_INVENTORY_MEASUREMENT"])) &&
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openViewModal(m.id); // open view modal
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted" />{" "}
                    View
                  </DropdownItem>
                }
                {IsValidPermissions(["EDIT_INVENTORY_MEASUREMENT"]) &&
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openEditModal(m.id); // open modal for edit
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted" />{" "}
                    Edit
                  </DropdownItem>
                }
                {IsValidPermissions(["EDIT_INVENTORY_MEASUREMENT"]) && (
                  <DropdownItem className="text-danger" onClick={() => handleDelete(m)}>
                    <i className="ri-delete-bin-line align-bottom me-2" />
                    Delete
                  </DropdownItem>
                )}

                {/* <DropdownItem divider />
                <DropdownItem
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // delete flow (implement if needed)
                  }}
                >
                  <i className="ri-delete-bin-fill align-bottom me-2 text-muted" />{" "}
                  Delete
                </DropdownItem> */}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  // Quick fix: mark columns non-sortable by default (so header has no sort toggle)
  const columns = useMemo(
    () => baseColumns.map((c) => ({ ...c, disableSortBy: true })),
    [baseColumns]
  );

  document.title = "Measurements | Resource Inventory";

  if (loadingMeasurements) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Measurements" pageTitle="Resource Inventory" />
          <div className="text-center py-5">
            <Loader />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Measurements" pageTitle="Resource Inventory" />

        <Row>
          <Col xl={12}>
            <Card>
              <div className="card-header border-0 d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Measurements</h5>
                <div className="d-flex align-items-center gap-2">
                  <Input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search measurements..."
                    aria-label="Search measurements"
                  />
                  {IsValidPermissions(["ADD_INVENTORY_MEASUREMENT"]) &&
                    <Button color="primary" onClick={openCreateModal}>
                      Add Measurement
                    </Button>
                  }
                </div>
              </div>

              <div className="card-body pt-0">
                {data && data?.length > 0 ? (
                  <TableContainer
                    columns={columns}
                    data={data}
                    isGlobalFilter={false}
                    customPageSize={pageState.pageSize}
                    divClass="table-responsive mb-1"
                    tableClass="mb-0 align-middle table-borderless"
                    theadClass="table-light text-muted"
                    SearchPlaceholder="Search Measurements..."
                    isPagination={false}
                    isServerPagination
                    serverPaginationProps={measurementsResponse}
                    setPageState={setPageState}
                  />
                ) : (
                  <div className="py-4 text-center">
                    <div>
                      <i className="ri-search-line display-5 text-success" />
                    </div>
                    <div className="mt-4">
                      <h5>Sorry! No Measurements Found</h5>
                      <p className="text-muted">
                        Click "Add Measurement" to create one.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Edit/Create Modal — NO ModalFooter */}
        <Modal isOpen={showEditModal} toggle={closeEditModal} size="lg">
          <ModalHeader toggle={closeEditModal}>
            {isEditMode ? "Edit Measurement" : "Create Measurement"}
          </ModalHeader>
          <ModalBody>
            <MeasurementEditView
              measurementId={currentMeasurementId ?? undefined}
              compact={true}
              onSaved={handleSaved}
            />
          </ModalBody>
          {/* intentionally no ModalFooter here */}
        </Modal>

        {/* View Modal */}
        <Modal isOpen={isViewModalOpen} toggle={closeViewModal} size="lg">
          <ModalHeader toggle={closeViewModal}>Measurement Details</ModalHeader>
          <ModalBody>
            {fetchingMeasurementDetail ? (
              <div className="text-center py-4">
                <Loader />
              </div>
            ) : measurementDetail ? (
              <div className="table-card">
                <table className="table mb-0">
                  <tbody>
                    <tr>
                      <td className="fw-medium">Name</td>
                      <td>{measurementDetail.name}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Symbol</td>
                      <td>{measurementDetail.symbol ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Description</td>
                      <td>{measurementDetail.description ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Created</td>
                      <td>
                        {new Date(
                          measurementDetail.created_at
                        ).toLocaleString()}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Modified</td>
                      <td>
                        {measurementDetail.modified_at
                          ? new Date(
                            measurementDetail.modified_at
                          ).toLocaleString()
                          : "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No details available.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeViewModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default MeasurementsList;
